import React, { FC, useCallback, useState } from "react";
import EChartsReact from "echarts-for-react";
import { trafficLight } from "../../theme";
import { IDamsList, ISummaryCriticalHazards, ISocietalRisk } from "../../types/types";
import { makeStyles, withStyles } from '@material-ui/core/styles';
import {
    Accordion,
    AccordionSummary, ClickAwayListener,
    Divider, Grow, IconButton, MenuItem, Drawer,
    Paper, Popper, Toolbar, Grid, Tooltip
} from "@material-ui/core";
import { CSVLink, CSVDownload } from "react-csv";
import GetAppIcon from '@material-ui/icons/GetApp';
import NotesIcon from '@material-ui/icons/Notes';


import { toPng } from "html-to-image";
import downloadjs from "downloadjs";

const useStyles = makeStyles({

    grid: {
        display: 'grid',
        gridTemplateColumns: 'max-content repeat(11,1fr)',
        padding: '10px 15% 10px 15%',
        justifyContent: 'center',

    },

    item: {
        display: 'flex',
        justifyContent: 'center',
        borderBottom: '1px solid lightgrey',
        alignItems: 'center',
        fontSize: '12px',
        padding: '0px 2px',
    },
    item2: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '50%',
        width: '17px',
        height: '17px',
        margin: '2px 0px',
        '&:hover': {
            transform: 'scale(1.0)',
            boxShadow: '0px 0px 5px 0px black',

        }
    },
    title: {
        display: 'flex',
        justifyContent: 'right',
        alignItems: 'center',

        fontSize: '12px',
        padding: '0px 2px',

    },

    label: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderLeft: '1px solid black',
        borderTop: '1px solid black',
        borderBottom: '1px solid black',

        fontSize: '12px',
        padding: '10px 5px',
        // writingMode: 'vertical-rl',
        // WebkitTransform: 'rotate(180deg)',




    },

    label2: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderLeft: '1px solid black',
        borderRight: 'px solid black',
        borderTop: '1px solid black',
        padding: '10px 5px',
        fontSize: '12px',
        fontWeight: 'bold',
    },
    overlap: {
        gridArea: '1 / 1 / 10 / 10',
        position: 'relative',
        ZIndex: '1',
    }

});

const LightTooltip = withStyles((theme) => ({
    tooltip: {
        backgroundColor: theme.palette.common.white,
        color: 'rgba(0, 0, 0, 0.87)',
        boxShadow: theme.shadows[1],
        fontSize: 14,
    },
}))(Tooltip);



interface IFMChartProps {
    criticalHazards: ISummaryCriticalHazards[],
    damList: IDamsList[],
    risk_assessments: ISocietalRisk[]
}


const HeatMapTableCH: FC<IFMChartProps> = ({ criticalHazards, damList, risk_assessments }) => {

    const classes = useStyles();

    const groups = Array.from(new Set(criticalHazards.map(
        s => s.societal_confidence_id)))

    const assessment_ids = Array.from(new Set(criticalHazards.map(
        s => s.assessment_project_id)))

    const find_length = (array: number[], levels: number[]): number[][] => {

        return levels.map(level => {
            const count = array.filter(s => s === level).length
            return [count, level]
        })

    }

    const handleCaptureClick = useCallback(() => {

        const pricingTableElmt = document.querySelector<HTMLElement>('.ch-table')
        if (!pricingTableElmt) return;
        toPng(pricingTableElmt, { pixelRatio: 4, cacheBust: true })
            .then((dataUrl: any) => {
                downloadjs(dataUrl, 'Critical_Hazzards.png');
            })
            .catch((err) => {
                console.log(err)
            })
    }, [])


    const data: any[][] = assessment_ids.map(id => {

        const dam_name = damList.find(d => d.id === id)?.dam.name;

        // get the societal level data for the dam
        let soc_level: any = risk_assessments.filter(dam => dam.assessment_project_id === id)


        // there is the potential for a dam to not have any data
        let societal_risk_value: number | string = 0
        if (soc_level.length > 0) {
            let societal_risk_value_number = soc_level[0].societal_risk_before
            // societal_risk_value = 
            societal_risk_value = societal_risk_value_number.toExponential(2)
        }

        const assessments = criticalHazards.filter(s => s.assessment_project_id === id);

        const societal_confidence_id = assessments.map(s => s.societal_confidence_id);

        const individual_confidence_id = assessments.map(s => s.individual_confidence_id);

        // convet the ids into lengths
        const societal_confidence_length: number[][] = find_length(societal_confidence_id, [1, 2, 3, 4, 5]);
        const individual_confidence_length: number[][] = find_length(individual_confidence_id, [1, 2, 3, 4, 5]);

        return [dam_name, societal_risk_value, ...societal_confidence_length, ...individual_confidence_length]
    })

    //sort data by societal impact
    data.sort((a, b) => {
        return b[1] - a[1]
    })

    data.forEach(d => {
        if (d[1] === 0) {
            d[1] = 'Undefined'
        }
    })

    const count_data = data.map(dam => {
        return dam.map(d => {
            // if d is an array
            if (Array.isArray(d)) {
                return d[0]
            }
            else {
                return d
            }
        })
    })

    const csvData = [['dam name', 'societal impact', 'Societal - Very Strong', 'Societal - Strong', 'Societal - Weak', 'Societal - Very Weak', 'Societal - Undefined', 'Individual - Very Strong', 'Individual - Strong', 'Individual - Weak', 'Individual - Very Weak', 'Individual - Undefined'], ...count_data]

    console.log(csvData)

    const subgroups: string[] = ['Very Strong', 'Strong', 'Weak', 'Very Weak', 'Undefined', 'Very Strong', 'Strong', 'Weak', 'Very Weak', 'Undefined']

    const setColor = (level: any, def: number) => {

        if (def === 0) {
            return 'white'
        }

        if (level === 1) {
            return trafficLight.vgreen
        }
        if (level === 2) {
            return trafficLight.bgreen
        }
        if (level === 3) {
            return trafficLight.byellow
        }
        if (level === 4) {
            return trafficLight.bred
        }
        if (level === 5) {
            return trafficLight.grey
        }

    }

    const setName = (level: any) => {

        if (level === 1) {
            return 'Very Strong'
        }
        if (level === 2) {
            return 'Strong'
        }
        if (level === 3) {
            return 'Weak'
        }
        if (level === 4) {
            return 'Very Weak'
        }
        if (level === 5) {
            return 'Undefined'
        }
    }


    return (
        <>
            <div style={{ padding: "10px" }}>
                <Paper variant={"outlined"} className={'table-section.' + ' ch-table'}>
                    <Grid container>
                        <Grid item xs={1}></Grid>
                        <Grid item xs={10}>
                            <p style={{ fontSize: "20px", position: 'relative', top: '-10px' }}><strong>Confidence of Critical Hazards being below Safety Threshold</strong></p>
                            <p style={{ fontSize: "12px", position: 'relative', top: '-20px' }}>Number of grouped failure modes</p>
                        </Grid>
                        <Grid item xs={1}>
                            <IconButton aria-label="download" onClick={handleCaptureClick}>
                                <GetAppIcon />
                            </IconButton>
                            <CSVLink data={csvData} filename={"Critical_Hazzards.csv"}>
                                <IconButton aria-label="download">
                                    <NotesIcon />
                                </IconButton>
                            </CSVLink>
                        </Grid>
                    </Grid>

                    <div className={classes.grid}>
                        <div className={classes.title} > </div>
                        <div className={classes.label2} style={{ gridRow: "1/ span 2",gridColumn: "2/3", borderLeft: "1px black solid", borderRight: "0px black solid", borderBottom: '1px solid black', }} > Societal risk (live/year) </div>
                        <div className={classes.label2} style={{ gridColumn: "3/8", borderLeft: "1px black solid" }}>Societal risk controls</div>
                        <div className={classes.label2} style={{ gridColumn: "8/13", borderRight: "1px black solid" }}>Individual risk controls</div>
                        <div className={classes.title} > </div>
                        
                        {
                            subgroups.map((subgroup,index) => {
                                if (index === 9) {
                                    return <div className={classes.label} style={{ borderRight: 'solid 1px Black' }}>{subgroup}</div>
                                }
                                else {
                                    return <div className={classes.label}>{subgroup}</div>
                                }
                            })
                        }
                        {
                            data.map((dam, ind) => {

                                let dam_name = ""
                                let top = ""
                                if (ind === 0) {
                                    top = "1px solid lightgrey"
                                }


                                const element = dam.map((d, index) => {

                                    let left = ""
                                    let right = "0px solid black"

                                    if (index === 1) {
                                        left = "1px solid black"
                                        right = ""
                                    }

                                    if (index === 2) {
                                        left = "1px solid black"
                                    }

                                    if (index === 7) {
                                        left = "1px solid black"
                                    }

                                    if (index === 11) {
                                        right = "1px solid black"
                                    }

                                    if (index === 0) {
                                        dam_name = d
                                        return <div className={classes.title}>{d}</div>
                                    }
                                    if (index === 1) {
                                        return (<div className={classes.item} style={{ borderTop: top, borderLeft: left, borderRight: right, color: setColor(6, d[0]) }}>
                                            {d} </div>
                                        )

                                    }
                                    else {
                                        return (
                                            <LightTooltip title={
                                                <div>
                                                    <b>{dam_name}</b> <br /> {setName(d)}
                                                </div>
                                            }>
                                                <div className={classes.item} style={{ borderLeft: left, borderTop: top, borderRight: right, color: setColor(6, d[0]) }} >

                                                    {d[0] !== 0
                                                        ? <div className={classes.item2} style={{ backgroundColor: setColor(d[1], d[0]) }}>{d[0]}</div>
                                                        : ""}

                                                </div>
                                            </LightTooltip>)

                                    }
                                })

                                return element
                            })
                        }




                    </div>
                </Paper>
            </div>



        </>

    )

}


export default HeatMapTableCH;